import React from 'react';
import { Music, Star, Mic, Users, Disc3 } from 'lucide-react';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { useSanityProducts } from '../hooks/useSanityProducts';
import { useTranslation } from '../context/TranslationContext';
import arrangementsText from '../data/arrangementsText';

// Slugs that ARE in sheet-music category but are NOT real arrangements
var EXCLUDED_SLUGS = ['tuning-chart', 'art-in-trumpet-album'];

// Tracks from the "Art in Trumpet" 2018 album (priority order matching album track listing)
var ART_IN_TRUMPET_SLUGS = [
  'chanson-arabe',
  'danse-macabre',
  'romanian-folk-dances',
  'hora-staccato',
  'arabesque-no-1',
  'danse-espagnol',
  'mendelssohn-violin-concerto',
  'campanella'
];

// Solo / Acapella arrangements (unaccompanied)
var SOLO_SLUGS = ['malaguena-solo', 'green-hornet-acapella'];

// Big Band arrangements
var BIG_BAND_SLUGS = ['green-hornet-big-band'];

// Spotify's iframe embed occasionally crashes inside its own Next.js bundle
// (RangeError: Incorrect locale information provided). Our global error shim
// in src/index.js swallows the cross-origin noise so the React dev overlay
// never pops because of it.
var SPOTIFY_ALBUM_EMBED = 'https://open.spotify.com/embed/album/0FNNxh9GLN9B8ZegTYiCLt?utm_source=generator&theme=0';
var SPOTIFY_ALBUM_URL = 'https://open.spotify.com/album/0FNNxh9GLN9B8ZegTYiCLt';
var ART_IN_TRUMPET_COVER = 'https://cdn.sanity.io/images/5mllso8r/production/16d797b8d8aed428260450cb146449fe3cd79771-1600x1600.jpg';

function isArrangement(p) {
  if (!p) return false;
  // Arrangements live under either 'sheet-music' (legacy) or 'digital' (new). Both
  // get sliced down by excluding the bound Real Book imprints below.
  var cat = p.category || '';
  if (cat !== 'sheet-music' && cat !== 'digital') return false;
  var slug = p.slug || '';
  if (slug.indexOf('real-book') !== -1) return false;
  if (slug.indexOf('new-real-book') !== -1) return false;
  if (slug.indexOf('real-christmas') !== -1) return false;
  if (EXCLUDED_SLUGS.indexOf(slug) !== -1) return false;
  return true;
}

function isAlbumTrack(p) { return ART_IN_TRUMPET_SLUGS.indexOf(p.slug || '') !== -1; }
function isSolo(p) { return SOLO_SLUGS.indexOf(p.slug || '') !== -1; }
function isBigBand(p) { return BIG_BAND_SLUGS.indexOf(p.slug || '') !== -1; }

function SectionBlock(props) {
  var icon = props.icon;
  var title = props.title;
  var subtitle = props.subtitle;
  var products = props.products;
  var testId = props.testId;
  var children = props.children;

  if (!products || products.length === 0) return null;

  var cards = [];
  for (var i = 0; i < products.length; i++) {
    cards.push(React.createElement(ProductCard, { key: products[i].slug, product: products[i], index: i, imageFit: 'contain' }));
  }

  return React.createElement('div', { className: 'mb-16', 'data-testid': testId },
    React.createElement('div', { className: 'flex items-center gap-4 mb-3' },
      React.createElement(icon, { className: 'w-6 h-6 text-[#d4af37]' }),
      React.createElement('h2', { className: 'text-2xl md:text-3xl font-bold text-white' }, title),
      React.createElement('span', { className: 'px-3 py-1 rounded-full bg-neutral-800 text-neutral-400 text-sm' }, products.length)
    ),
    subtitle ? React.createElement('p', { className: 'text-neutral-500 text-sm mb-6 ml-10' }, subtitle) : null,
    React.createElement('div', { className: 'h-px bg-gradient-to-r from-[#d4af37]/40 via-[#d4af37]/20 to-transparent mb-8' }),
    children,
    React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' }, cards)
  );
}

function ArrangementsPage() {
  var ref = useSanityProducts({});
  var products = ref.products;
  var loading = ref.loading;
  var langRef = useTranslation();
  var language = langRef.language;
  var tx = arrangementsText[language] || arrangementsText.en;

  // Bucket products
  var albumMap = {};
  var asPlayed = [];
  var solo = [];
  var bigBand = [];
  for (var i = 0; i < products.length; i++) {
    var p = products[i];
    if (!isArrangement(p)) continue;
    if (isAlbumTrack(p)) {
      albumMap[p.slug] = p;
    } else if (isBigBand(p)) {
      bigBand.push(p);
    } else if (isSolo(p)) {
      solo.push(p);
    } else {
      asPlayed.push(p);
    }
  }

  // Order album tracks per ART_IN_TRUMPET_SLUGS sequence
  var albumTracks = [];
  for (var a = 0; a < ART_IN_TRUMPET_SLUGS.length; a++) {
    var trackSlug = ART_IN_TRUMPET_SLUGS[a];
    if (albumMap[trackSlug]) albumTracks.push(albumMap[trackSlug]);
  }

  function sortByName(a, b) { return (a.name || '').localeCompare(b.name || ''); }
  asPlayed.sort(sortByName);
  solo.sort(sortByName);
  bigBand.sort(sortByName);

  // Inline Spotify player. The error shim in src/index.js silently swallows
  // any cross-origin spotifycdn / RangeError noise so a glitch inside the
  // iframe never pops the React dev overlay.
  var spotifyEmbed = React.createElement('div', { className: 'mb-10 rounded-2xl overflow-hidden border border-[#1db954]/30 bg-neutral-900/40 shadow-[0_8px_40px_-12px_rgba(29,185,84,0.25)]' },
    React.createElement('iframe', {
      'data-testid': 'art-in-trumpet-spotify-embed',
      src: SPOTIFY_ALBUM_EMBED,
      width: '100%',
      height: '352',
      frameBorder: '0',
      allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture',
      loading: 'lazy',
      title: 'Art in Trumpet - Isaías Jiménez (Spotify)',
      style: { display: 'block', border: 0 }
    })
  );

  var spotifyButton = React.createElement('a', {
    href: SPOTIFY_ALBUM_URL,
    target: '_blank',
    rel: 'noopener noreferrer',
    'data-testid': 'art-in-trumpet-spotify-link',
    className: 'inline-flex items-center gap-2 mb-8 px-5 py-2.5 rounded-full bg-[#1db954] text-black text-sm font-semibold hover:bg-[#1ed760] transition-colors'
  },
    React.createElement(Disc3, { className: 'w-4 h-4' }),
    tx.listenOnSpotify
  );

  var content;
  if (loading) {
    content = React.createElement('div', { className: 'text-center py-24' },
      React.createElement('p', { className: 'text-neutral-500' }, '...')
    );
  } else {
    content = React.createElement('div', null,
      React.createElement(SectionBlock, {
        icon: Disc3,
        title: tx.albumTitle,
        subtitle: tx.albumSubtitle,
        products: albumTracks,
        testId: 'arrangements-section-album'
      }, spotifyEmbed, spotifyButton),
      React.createElement(SectionBlock, {
        icon: Star,
        title: tx.asPlayedTitle,
        subtitle: tx.asPlayedBody,
        products: asPlayed,
        testId: 'arrangements-section-as-played'
      }),
      React.createElement(SectionBlock, {
        icon: Mic,
        title: tx.soloTitle,
        subtitle: tx.soloBody,
        products: solo,
        testId: 'arrangements-section-solo'
      }),
      React.createElement(SectionBlock, {
        icon: Users,
        title: tx.bigBandTitle,
        subtitle: tx.bigBandBody,
        products: bigBand,
        testId: 'arrangements-section-big-band'
      })
    );
  }

  return React.createElement(Layout, null,
    React.createElement(SEO, {
      title: 'Trumpet & Piano Arrangements – Sheet Music by Isaias Jimenez',
      description: 'Digital sheet music arrangements for trumpet and piano. Featuring the Art in Trumpet album scores plus virtuoso transcriptions of Paganini, Ravel, Debussy, Piazzolla, Brubeck and more.',
      url: '/arrangements'
    }),
    React.createElement('section', { className: 'pt-32 pb-12 bg-gradient-to-b from-neutral-900 to-black' },
      React.createElement('div', { className: 'max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12' },
        React.createElement('img', {
          src: ART_IN_TRUMPET_COVER,
          alt: 'Art in Trumpet — album cover by Isaías Jiménez',
          'data-testid': 'art-in-trumpet-cover',
          className: 'w-48 h-48 md:w-64 md:h-64 rounded-2xl object-cover shadow-2xl shadow-[#d4af37]/10 flex-shrink-0 border border-white/10',
          referrerPolicy: 'no-referrer'
        }),
        React.createElement('div', { className: 'flex-1 text-center md:text-left' },
          React.createElement('p', { className: 'text-sm tracking-widest uppercase text-[#d4af37] mb-4' }, tx.eyebrow),
          React.createElement('h1', { className: 'text-5xl md:text-7xl font-bold text-white mb-6' }, tx.title),
          React.createElement('p', { className: 'text-xl text-neutral-400 max-w-3xl' }, tx.subtitle)
        )
      )
    ),
    React.createElement('section', { className: 'pb-12 bg-black' },
      React.createElement('div', { className: 'max-w-4xl mx-auto px-6 lg:px-12' },
        React.createElement('div', { className: 'bg-neutral-900/60 border border-white/10 rounded-2xl p-8 md:p-10' },
          React.createElement('div', { className: 'flex items-center gap-3 mb-4' },
            React.createElement(Music, { className: 'w-6 h-6 text-[#d4af37]' }),
            React.createElement('h2', { className: 'text-2xl font-bold text-white' }, tx.aboutTitle)
          ),
          React.createElement('p', { className: 'text-neutral-300 leading-relaxed' }, tx.aboutBody)
        )
      )
    ),
    React.createElement('section', { 'data-testid': 'arrangements-page', className: 'py-16 bg-black border-t border-white/5' },
      React.createElement('div', { className: 'max-w-7xl mx-auto px-6 lg:px-12' }, content)
    )
  );
}

export default ArrangementsPage;
